import { SpanContext, SpanStatusCode } from '@opentelemetry/api';
import { AxiosRequestConfig } from 'axios';
import { DEFAULT_RPS_PORT, RPS_PAYMENT_URL } from 'src/common/helpers/config';
import XLRLogger from 'src/common/helpers/logger';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { EpsData, PaymentMessage } from 'src/devices/payment/dtos/eps-data';
import { getMetaData } from 'src/xlr-configuration/pos-service-type-utils';
import {
  initializePaymentConnection,
  pinpadConnection,
  sendPaymentMessage,
  sendPaymentRequest,
} from '@ncr-retail/common-payment-operations';
import { getTouchpointData } from './nmp-handler';
import { PaymentException } from 'src/common/exceptions/paymentException';
import { updatePinPadStatus } from './pinpad-operations';
import { store } from 'src/common/redux/configure-store';
import { PinPadActions } from 'src/common/redux/reducers/pinpad-reducer';

export interface RpsPaymentMessage {
  code: RpsPaymentMessageCodes;
  message?: EpsData;
}

export enum RpsPaymentMessageCodes {
  OPEN_WS = 'OPEN_WS',
  GET_CARD_DETAILS = 'GET_CARD_DETAILS',
  GET_TOUCHPOINT_DATA = 'GET_TOUCHPOINT_DATA',
}

export interface RpsConfig {
  defaultRpsPort?: string | number;
  rpsHostName?: string;
}

const loggerSource = 'devices/payment-controller.ts';

export const emeraldSendPaymentRequest = async (
  name: string,
  message: PaymentMessage,
  parentSpanContext?: SpanContext,
): Promise<PaymentMessage> => {
  const span = createSpan(`[RPS] send-payment-request ${name}`, parentSpanContext, true);
  const { RPS_PORT = DEFAULT_RPS_PORT, RPS_HOSTNAME = RPS_PAYMENT_URL } = getMetaData().xlrConfig;
  const options: AxiosRequestConfig = {
    method: 'POST',
    url: `/${name}`,
    baseURL: `http://${RPS_HOSTNAME}:${RPS_PORT}/payment`,
    data: message,
  };
  const optionsLogger = JSON.parse(
    JSON.stringify(options, (key, value) => {
      if (key.includes('clientKey') || key.includes('secretKey')) {
        return '*'.repeat(value.length);
      }
      return value;
    }),
  );

  XLRLogger.info({
    message: 'RPS Payment Request',
    source: loggerSource,
    data: optionsLogger,
  });

  const rpsConfig: RpsConfig = {
    defaultRpsPort: DEFAULT_RPS_PORT,
    rpsHostName: RPS_PAYMENT_URL,
  };

  return sendPaymentRequest(name, message, rpsConfig)
    .then(data => {
      return data;
    })
    .catch(err => {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: 'Send Payment Request Error',
      });
      XLRLogger.error({
        message: 'RPS Payment Response',
        source: 'devices/payment/payment-controller.ts',
        data: err,
      });
      throw new PaymentException(err.response?.data || err);
    })
    .finally(() => endSpan(span));
};

export const emeraldPaymentMessage = (
  code: RpsPaymentMessageCodes,
  message?: PaymentMessage,
  parentSpanContext?: SpanContext,
) => {
  // TODO: Cypress stub these so we can dip in that integration test sauce
  const span = createSpan('[RPS] send-payment-message', parentSpanContext, true);
  if (!window.Cypress) {
    sendPaymentMessage(code, message);
  }
  const data = { code, message };
  XLRLogger.debug({
    message: 'Outgoing RPS Payment WS Message: ',
    source: loggerSource,
    data,
  });
  endSpan(span);
};

const onOpenCallback = () => {
  XLRLogger.info({
    message: 'RPS Payment WS Connection Opened',
    source: loggerSource,
  });
};

const onCloseCallback = () => {
  XLRLogger.info({
    message: 'RPS Payment WS Connection Closed',
    source: loggerSource,
  });

  updatePinPadStatus(true, false);
  store.dispatch(PinPadActions.setIsPinpadConfigured(false));
};

const onErrorCallback = (err: any) => {
  XLRLogger.error({
    message: 'RPS Payment WS Error',
    source: loggerSource,
    data: err,
  });
};

export const emeraldInitializePayment = async (parentSpanContext?: SpanContext): Promise<void> => {
  const span = createSpan('[RPS] initialize-payment-connection', parentSpanContext, true);
  const { RPS_PORT = DEFAULT_RPS_PORT, RPS_HOSTNAME = RPS_PAYMENT_URL } = getMetaData().xlrConfig;
  const rpsConfig: RpsConfig = {
    defaultRpsPort: RPS_PORT,
    rpsHostName: RPS_HOSTNAME,
  };
  const message = getTouchpointData();
  initializePaymentConnection(rpsConfig, message, onOpenCallback, onCloseCallback, onErrorCallback)
    .catch(err => {
      XLRLogger.error({
        message: 'RPS Payment WS Failed to Connect',
        source: loggerSource,
        data: err,
      });
    })
    .finally(() => endSpan(span));
};

export const emeraldRpsConnect = (parentSpanContext?: SpanContext): Promise<void> => {
  const wsSpan = createSpan('[RPS] Connect WebSocket', parentSpanContext, true);
  const { RPS_PORT = DEFAULT_RPS_PORT, RPS_HOSTNAME = RPS_PAYMENT_URL } = getMetaData().xlrConfig;
  const rpsConfig: RpsConfig = {
    defaultRpsPort: RPS_PORT,
    rpsHostName: RPS_HOSTNAME,
  };
  const message = getTouchpointData();

  return pinpadConnection(
    rpsConfig,
    message,
    onOpenCallback,
    onCloseCallback,
    onErrorCallback,
  ).finally(() => endSpan(wsSpan));
};
